import moment from 'moment';

export const PERIOD_PRESETS = [
  { key: 'last12', label: 'Last 12 Months', months: 12 },
  { key: 'last6', label: 'Last 6 Months', months: 6 },
  { key: 'last3', label: 'Last 3 Months', months: 3 },
  { key: 'last1', label: 'Last Month', months: 1 },
];

/**
 * Clamp a period so start is not before minMonth (YYYY-MM).
 * @param {{ start: string, end: string }} period
 * @param {string|null} minMonth
 * @returns {{ start: string, end: string }}
 */
export const clampPeriodToMinMonth = (period, minMonth) => {
  if (!period?.start || !period?.end || !minMonth) return period;
  let { start, end } = period;
  if (start < minMonth) start = minMonth;
  if (end < minMonth) end = minMonth;
  if (start > end) start = end;
  return { start, end };
};

export const getPresetPeriod = (months, minMonth = null) => {
  let period;
  if (months === 1) {
    const lastMonth = moment().subtract(1, 'month').startOf('month');
    period = {
      start: lastMonth.format('YYYY-MM'),
      end: lastMonth.format('YYYY-MM'),
    };
  } else {
    const end = moment().startOf('month');
    const start = end.clone().subtract(months - 1, 'months');
    period = {
      start: start.format('YYYY-MM'),
      end: end.format('YYYY-MM'),
    };
  }
  return clampPeriodToMinMonth(period, minMonth);
};

/**
 * Default period: last 12 months (matches wire preset).
 * @returns {{ start: string, end: string }}
 */
export const getDefaultPeriodRange = () => getPresetPeriod(12);

/**
 * @param {{ start: string, end: string }|null} period
 * @returns {string} preset key or 'custom'
 */
export const getMatchingPresetKey = (period, minMonth = null) => {
  if (!period?.start || !period?.end) return 'custom';
  const match = PERIOD_PRESETS.find(preset => {
    const range = getPresetPeriod(preset.months, minMonth);
    return range.start === period.start && range.end === period.end;
  });
  return match?.key ?? 'custom';
};

/**
 * Convert hubCreatedAt (ISO/date) to YYYY-MM for month inputs.
 * @param {string|null} hubCreatedAt
 * @returns {string|null}
 */
export const getMinSelectableMonth = hubCreatedAt => {
  if (!hubCreatedAt) return null;
  const m = moment(hubCreatedAt);
  return m.isValid() ? m.format('YYYY-MM') : null;
};

export const getMaxSelectableMonth = () => moment().format('YYYY-MM');

/**
 * Format API query params from stored period.
 * @param {{ start: string, end: string }} period
 * @returns {{ startDate: string, endDate: string }|null}
 */
export const getPeriodQueryParams = period => {
  if (!period?.start || !period?.end) return null;
  return {
    startDate: moment(period.start, 'YYYY-MM')
      .startOf('month')
      .format('YYYY-MM-DD'),
    endDate: moment(period.end, 'YYYY-MM')
      .startOf('month')
      .format('YYYY-MM-DD'),
  };
};

/**
 * Human-readable period label (e.g. "Jan 2026 – Jul 2026").
 * @param {{ start: string, end: string }|null} period
 * @param {string} [fallback]
 * @returns {string}
 */
export const formatPeriodLabel = (period, fallback = '') => {
  if (!period?.start || !period?.end) return fallback;
  const start = moment(period.start, 'YYYY-MM');
  const end = moment(period.end, 'YYYY-MM');
  if (start.isSame(end, 'month')) {
    return start.format('MMM YYYY');
  }
  return `${start.format('MMM YYYY')} – ${end.format('MMM YYYY')}`;
};

/**
 * Prior period of equal length immediately before the selected range.
 * @param {{ start: string, end: string }} period
 * @returns {{ start: string, end: string }|null}
 */
export const getPriorPeriodRange = period => {
  if (!period?.start || !period?.end) return null;
  const start = moment(period.start, 'YYYY-MM').startOf('month');
  const end = moment(period.end, 'YYYY-MM').startOf('month');
  const monthCount = end.diff(start, 'months') + 1;
  const priorEnd = start.clone().subtract(1, 'month');
  const priorStart = start.clone().subtract(monthCount, 'months');
  return {
    start: priorStart.format('YYYY-MM'),
    end: priorEnd.format('YYYY-MM'),
  };
};

/**
 * Normalize a month value to YYYY-MM.
 * @param {string|moment.Moment|Date} value
 * @returns {string|null}
 */
export const normalizeMonthValue = value => {
  if (!value) return null;
  if (moment.isMoment(value) || value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM') : null;
  }
  const asString = String(value).trim();
  if (/^\d{4}-\d{2}$/.test(asString)) return asString;
  const parsed = moment(asString);
  return parsed.isValid() ? parsed.format('YYYY-MM') : null;
};

/**
 * Whether start month is on or before end month.
 * @param {{ start: string, end: string }} period
 * @returns {boolean}
 */
export const isValidPeriodRange = (period, minMonth = null) => {
  const start = normalizeMonthValue(period?.start);
  const end = normalizeMonthValue(period?.end);
  if (!start || !end) return false;
  if (moment(start, 'YYYY-MM').isAfter(moment(end, 'YYYY-MM'), 'month')) {
    return false;
  }
  if (minMonth && start < minMonth) return false;
  return true;
};

/**
 * Normalize a period object to YYYY-MM start/end.
 * @param {{ start: string, end: string }} period
 * @returns {{ start: string, end: string }|null}
 */
export const normalizePeriodRange = period => {
  const start = normalizeMonthValue(period?.start);
  const end = normalizeMonthValue(period?.end);
  if (!start || !end) return null;
  return { start, end };
};
